import { useVueI18n } from '../locales'
const t = useVueI18n()
export interface ScheduleRRuleOptions {
  intervalType: IntervalType | string
  interval: number
  startDate: string
  startTime?: string
  endDate?: string
  endTime?: string
  dayOfMonth?: number | string
  dayOfWeek?: ShortWeekDays | string
  numOfWeek?: number
  monthOfYear?: ShortMonths | string
  count?: number
  daysBefore?: number
  endType?: string
  useStartAsPrimaryUserSigned?: boolean
}

export enum EndType {
  NEVER = 'never',
  BY = 'by',
  AFTER = 'after',
}

export enum IntervalType {
  YEARLY = 'yearly',
  MONTHLY = 'monthly',
  WEEKLY = 'weekly',
  DAILY = 'daily',
  HOURLY = 'hourly',
  MINUTELY = 'minutely',
}

/** Default rrule when "Generate invoice at the time of signing" is ON. */
export const DEFAULT_RRULE_INVOICE_AT_SIGNING: ScheduleRRuleOptions = {
  intervalType: IntervalType.WEEKLY,
  interval: 0,
  startDate: '',
  endType: EndType.NEVER,
  useStartAsPrimaryUserSigned: true,
  daysBefore: 0,
  dayOfWeek: '',
}

export enum PriceItemIntervalType {
  'month' = IntervalType.MONTHLY,
  'year' = IntervalType.YEARLY,
  'week' = IntervalType.WEEKLY,
  'day' = IntervalType.DAILY,
  'hour' = IntervalType.HOURLY,
}

export enum ShortMonths {
  JAN = 'jan',
  FEB = 'feb',
  MAR = 'mar',
  APR = 'apr',
  MAY = 'may',
  JUN = 'jun',
  JUL = 'jul',
  AUG = 'aug',
  SEP = 'sep',
  OCT = 'oct',
  NOV = 'nov',
  DEC = 'dec',
}

export enum ShortWeekDays {
  MO = 'mo',
  TU = 'tu',
  WE = 'we',
  TH = 'th',
  FR = 'fr',
  SA = 'sa',
  SU = 'su',
}

export const MONTH_NAMES = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
}

export const DAY_NAMES = {
  su: 0, // Sunday
  mo: 1, // Monday
  tu: 2, // Tuesday
  we: 3, // Wednesday
  th: 4, // Thursday
  fr: 5, // Friday
  sa: 6, // Saturday
}

export const SCHEDULING_OPTIONS = {
  INTERVAL_TYPE: [
    {
      value: IntervalType.MINUTELY,
      get label() { return t('schedulingOptions.intervalType.minutely') },
      internal: true,
    },
    {
      value: IntervalType.HOURLY,
      get label() { return t('schedulingOptions.intervalType.hourly') },
      internal: true,
    },
    {
      value: IntervalType.DAILY,
      get label() { return t('schedulingOptions.intervalType.daily') },
    },
    {
      value: IntervalType.WEEKLY,
      get label() { return t('schedulingOptions.intervalType.weekly') },
    },
    {
      value: IntervalType.MONTHLY,
      get label() { return t('schedulingOptions.intervalType.monthly') },
    },
    {
      value: IntervalType.YEARLY,
      get label() { return t('schedulingOptions.intervalType.yearly') },
    },
  ],
  WEEKDAYS: [
    {
      value: ShortWeekDays.MO,
      get label() { return t('schedulingOptions.weekdays.monday') },
    },
    {
      value: ShortWeekDays.TU,
      get label() { return t('schedulingOptions.weekdays.tuesday') },
    },
    {
      value: ShortWeekDays.WE,
      get label() { return t('schedulingOptions.weekdays.wednesday') },
    },
    {
      value: ShortWeekDays.TH,
      get label() { return t('schedulingOptions.weekdays.thursday') },
    },
    {
      value: ShortWeekDays.FR,
      get label() { return t('schedulingOptions.weekdays.friday') },
    },
    {
      value: ShortWeekDays.SA,
      get label() { return t('schedulingOptions.weekdays.saturday') },
    },
    {
      value: ShortWeekDays.SU,
      get label() { return t('schedulingOptions.weekdays.sunday') },
    },
  ],
  DAYS: [
    {
      value: 1,
      get label() { return t('schedulingOptions.days.1') },
    },
    {
      value: 2,
      get label() { return t('schedulingOptions.days.2') },
    },
    {
      value: 3,
      get label() { return t('schedulingOptions.days.3') },
    },
    {
      value: 4,
      get label() { return t('schedulingOptions.days.4') },
    },
    {
      value: 5,
      get label() { return t('schedulingOptions.days.5') },
    },
    {
      value: 6,
      get label() { return t('schedulingOptions.days.6') },
    },
    {
      value: 7,
      get label() { return t('schedulingOptions.days.7') },
    },
    {
      value: 8,
      get label() { return t('schedulingOptions.days.8') },
    },
    {
      value: 9,
      get label() { return t('schedulingOptions.days.9') },
    },
    {
      value: 10,
      get label() { return t('schedulingOptions.days.10') },
    },
    {
      value: 11,
      get label() { return t('schedulingOptions.days.11') },
    },
    {
      value: 12,
      get label() { return t('schedulingOptions.days.12') },
    },
    {
      value: 13,
      get label() { return t('schedulingOptions.days.13') },
    },
    {
      value: 14,
      get label() { return t('schedulingOptions.days.14') },
    },
    {
      value: 15,
      get label() { return t('schedulingOptions.days.15') },
    },
    {
      value: 16,
      get label() { return t('schedulingOptions.days.16') },
    },
    {
      value: 17,
      get label() { return t('schedulingOptions.days.17') },
    },
    {
      value: 18,
      get label() { return t('schedulingOptions.days.18') },
    },
    {
      value: 19,
      get label() { return t('schedulingOptions.days.19') },
    },
    {
      value: 20,
      get label() { return t('schedulingOptions.days.20') },
    },
    {
      value: 21,
      get label() { return t('schedulingOptions.days.21') },
    },
    {
      value: 22,
      get label() { return t('schedulingOptions.days.22') },
    },
    {
      value: 23,
      get label() { return t('schedulingOptions.days.23') },
    },
    {
      value: 24,
      get label() { return t('schedulingOptions.days.24') },
    },
    {
      value: 25,
      get label() { return t('schedulingOptions.days.25') },
    },
    {
      value: 26,
      get label() { return t('schedulingOptions.days.26') },
    },
    {
      value: 27,
      get label() { return t('schedulingOptions.days.27') },
    },
    {
      value: 28,
      get label() { return t('schedulingOptions.days.28') },
    },
    {
      value: 29,
      get label() { return t('schedulingOptions.days.29') },
    },
    {
      value: 30,
      get label() { return t('schedulingOptions.days.30') },
    },
    {
      value: 31,
      get label() { return t('schedulingOptions.days.31') },
    },
    {
      value: -1,
      get label() { return t('schedulingOptions.days.last') },
    },
  ],
  MONTHS: [
    {
      value: ShortMonths.JAN,
      get label() { return t('schedulingOptions.months.january') },
    },
    {
      value: ShortMonths.FEB,
      get label() { return t('schedulingOptions.months.february') },
    },
    {
      value: ShortMonths.MAR,
      get label() { return t('schedulingOptions.months.march') },
    },
    {
      value: ShortMonths.APR,
      get label() { return t('schedulingOptions.months.april') },
    },
    {
      value: ShortMonths.MAY,
      get label() { return t('schedulingOptions.months.may') },
    },
    {
      value: ShortMonths.JUN,
      get label() { return t('schedulingOptions.months.june') },
    },
    {
      value: ShortMonths.JUL,
      get label() { return t('schedulingOptions.months.july') },
    },
    {
      value: ShortMonths.AUG,
      get label() { return t('schedulingOptions.months.august') },
    },
    {
      value: ShortMonths.SEP,
      get label() { return t('schedulingOptions.months.september') },
    },
    {
      value: ShortMonths.OCT,
      get label() { return t('schedulingOptions.months.october') },
    },
    {
      value: ShortMonths.NOV,
      get label() { return t('schedulingOptions.months.november') },
    },
    {
      value: ShortMonths.DEC,
      get label() { return t('schedulingOptions.months.december') },
    },
  ],
}
